import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Button, CircularProgress, Stack, TextField } from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { useHistory, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AuthContext } from "../../../../helpers/AuthContext";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { UITableShell, UITableToolbar } from "../../../../components/ui";
import PipelineBoard from "./components/PipelineBoard";
import { fetchInstanceUsers } from "../../../../services/users";
import CreateProjectModal from "../orders/create-project-modal/CreateProjectModal";

const CRM_PIPELINE_PAGE_SIZE = 20;
const SALESPERSON_PAGE_SIZE = 15;

function userDisplayName(user) {
  return [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.name || user?.email || `User #${user?.id}`;
}

export default function InstancePipeline() {
  const { instanceUniqueId } = useParams();
  const history = useHistory();
  const { authState } = useContext(AuthContext);
  const [crmScope, setCrmScope] = useState("my");
  const [baseScope, setBaseScope] = useState("my");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [userInput, setUserInput] = useState("");
  const [usersPage, setUsersPage] = useState(0);
  const [usersTotal, setUsersTotal] = useState(0);
  const [boardUsers, setBoardUsers] = useState([]);
  const usersRequest = useRef(0);
  const usersBusy = useRef(false);
  const userListbox = useRef(null);
  const userScrollTop = useRef(0);
  const boardUsersRequest = useRef(null);
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);
  const isCrmManager = ["administrator", "admin", "manager", "super-administrator"].includes(String(authState?.role || "").toLowerCase());

  const loadUsersPage = useCallback(async (search, page, requestId) => {
    usersBusy.current = true;
    setUsersLoading(true);
    try {
      const result = await fetchInstanceUsers(instanceUniqueId, {
        role: "client-rep,order-rep,administrator,super-administrator",
        q: search,
        page,
        limit: SALESPERSON_PAGE_SIZE,
        withCount: true,
        sort: "first-name",
      });
      if (requestId !== usersRequest.current) return;
      setUsers((previous) => {
        const byId = new Map((page === 1 ? [] : previous).map((user) => [String(user.id), user]));
        result.listOfUsers.forEach((user) => byId.set(String(user.id), user));
        return Array.from(byId.values());
      });
      setUsersPage(page);
      setUsersTotal(result.totalCount);
    } finally {
      if (requestId === usersRequest.current) {
        usersBusy.current = false;
        setUsersLoading(false);
      }
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    const requestId = ++usersRequest.current;
    setUsers([]);
    setUsersPage(0);
    setUsersTotal(0);
    if (!usersOpen || !isCrmManager || !instanceUniqueId) {
      usersBusy.current = false;
      setUsersLoading(false);
      return undefined;
    }
    usersBusy.current = true;
    setUsersLoading(true);
    const timer = setTimeout(() => loadUsersPage(userSearch, 1, requestId), 300);
    return () => {
      clearTimeout(timer);
      usersRequest.current += 1;
    };
  }, [instanceUniqueId, isCrmManager, usersOpen, userSearch, loadUsersPage]);

  useEffect(() => {
    if (usersPage > 1 && userListbox.current) userListbox.current.scrollTop = userScrollTop.current;
  }, [users, usersPage]);

  const loadBoardUsers = useCallback(() => {
    if (!isCrmManager || !instanceUniqueId) return;
    if (boardUsersRequest.current?.instanceUniqueId === instanceUniqueId) return;
    const request = { instanceUniqueId };
    boardUsersRequest.current = request;
    fetchInstanceUsers(instanceUniqueId).then((list) => {
      if (boardUsersRequest.current === request) setBoardUsers(list);
    });
  }, [instanceUniqueId, isCrmManager]);

  const setScope = (scope) => {
    setCrmScope(scope);
    if (scope !== "user") setBaseScope(scope);
    if (scope !== "user") setSelectedUserId("");
    if (scope !== "user") {
      setSelectedUser(null);
      setUserInput("");
    }
  };

  return (
    <div className="instance-dashboard instance-pipeline-page">
      <InstanceLeftSidebar />
      <ToastContainer />

      <div className="rightSide">
        <Box
          sx={{
            p: { xs: 1, md: 2 },
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 60px)",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <UITableShell
            customLayout
            paperSx={{
              flex: 1,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              borderTop: "3px solid",
              borderTopColor: "primary.main",
              minHeight: 0,
              boxShadow: "0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px -1px rgba(0,0,0,0.07)",
            }}
          >
            <UITableToolbar sx={{ flex: 1, minHeight: 0, overflow: "hidden", backgroundColor: "var(--ec-color-surface, #fff)", display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1.25 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Button size="small" variant={crmScope === "my" ? "contained" : "outlined"} onClick={() => setScope("my")}>
                    My Pipeline
                  </Button>
                  {isCrmManager ? (
                    <>
                      <Button size="small" variant={crmScope === "all" ? "contained" : "outlined"} onClick={() => setScope("all")}>
                        All
                      </Button>
                      <Autocomplete
                        size="small"
                        open={usersOpen}
                        onOpen={() => setUsersOpen(true)}
                        onClose={() => setUsersOpen(false)}
                        options={selectedUser && !users.some((user) => String(user.id) === String(selectedUser.id)) ? [...users, selectedUser] : users}
                        value={selectedUser}
                        inputValue={userInput}
                        loading={usersLoading}
                        filterOptions={() => users}
                        getOptionLabel={userDisplayName}
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                        onInputChange={(_, value, reason) => {
                          setUserInput(value);
                          if ((reason === "input" || reason === "clear") && value !== userSearch) {
                            usersRequest.current += 1;
                            setUserSearch(value);
                          }
                        }}
                        onChange={(_, user) => {
                          const nextUserId = user ? String(user.id) : "";
                          setSelectedUser(user);
                          setSelectedUserId(nextUserId);
                          setCrmScope(nextUserId ? "user" : baseScope);
                        }}
                        ListboxProps={{
                          ref: userListbox,
                          style: { maxHeight: 320, overflowY: "auto" },
                          onScroll: (event) => {
                            const list = event.currentTarget;
                            if (!usersBusy.current && usersPage > 0 && usersPage * SALESPERSON_PAGE_SIZE < usersTotal &&
                                list.scrollHeight - list.scrollTop - list.clientHeight < 64) {
                              loadUsersPage(userSearch, usersPage + 1, usersRequest.current);
                              userScrollTop.current = list.scrollTop;
                            }
                          },
                        }}
                        renderOption={(props, user) => {
                          const { key, ...optionProps } = props;
                          return <li {...optionProps} key={user.id}>{userDisplayName(user)}{user.role ? ` - ${user.role}` : ""}</li>;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="View salesperson"
                            inputProps={{ ...params.inputProps, "aria-label": "View salesperson" }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: <>{usersLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>,
                            }}
                          />
                        )}
                        sx={{
                          width: 230,
                          "& .MuiInputBase-root": { minHeight: 32, fontSize: 14 },
                        }}
                      />
                    </>
                  ) : null}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  {isCrmManager ? (
                    <Button size="small" variant="outlined" startIcon={<TuneRoundedIcon />} onClick={() => history.push(`/instance/${instanceUniqueId}/settings/global-settings/company-settings/crm`)}>
                      Accountability Settings
                    </Button>
                  ) : null}
                  <Button size="small" variant="contained" onClick={() => setIsCreateProjectOpen(true)}>
                    + New Project
                  </Button>
                </Stack>
              </Stack>
              <PipelineBoard
                instanceUniqueId={instanceUniqueId}
                pageSize={CRM_PIPELINE_PAGE_SIZE}
                height="calc(100vh - 190px)"
                boardMode="crm"
                crmScope={crmScope}
                selectedUserId={crmScope === "user" ? selectedUserId : undefined}
                selectedUserName={selectedUser ? userDisplayName(selectedUser) : ""}
                currentUserId={authState?.id}
                isCrmManager={isCrmManager}
                users={boardUsers}
                onWorkspaceOpen={loadBoardUsers}
                refreshKey={boardRefreshKey}
                enableAutoRefresh
              />
            </UITableToolbar>
          </UITableShell>
        </Box>
      </div>
      <CreateProjectModal
        open={isCreateProjectOpen}
        context="crmPipeline"
        createdFrom="crm_board"
        leadOrigin="self_generated"
        onClose={() => setIsCreateProjectOpen(false)}
        onCreated={() => {
          setIsCreateProjectOpen(false);
          setBoardRefreshKey((current) => current + 1);
        }}
        customers={[]}
      />
    </div>
  );
}
